// =============================================================================
// Activity action keys — derived mechanically from a method + URL, so a route
// shipped tomorrow is logged with a readable key and nobody has to remember to
// add anything (docs/designs/USER_ACTIVITY_LOGS_AND_ANALYTICS.md §2).
//
// Pure functions, no DOM, no DB: imported by the browser tracker, the API
// routes and the tests alike.
// =============================================================================

const MUTATING_METHODS: ReadonlySet<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** The two query keys that identify a *view* inside one route (dashboard sections, workspace tabs). */
const PAGE_CONTEXT_KEYS = ['section', 'tab'] as const;

/**
 * `/api/*` prefixes the browser never logs.
 *   /api/auth/     — the session is missing or being re-signed; the auth routes
 *                    log these themselves (lib/activity/server.ts).
 *   /api/activity  — the log endpoint itself.
 *   the rest       — machine traffic (auto screenshots, debug taps), not people.
 */
const IGNORED_API_PREFIXES: readonly string[] = [
  '/api/auth/',
  '/api/activity',
  '/api/screenshot/',
  '/api/debug/',
  '/api/health',
  '/api/bridge-script',
  '/api/qa-runner/'
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** `PR-7` style sequence ids (workflow requests). */
const SEQUENCE_ID_PATTERN = /^[A-Z]{1,5}-\d+$/;
/** `bld_…`, `evt_…`, `job_…`, `it_…`, `mech_…`: the hand-minted workflow ids. */
const PREFIXED_ID_PATTERN = /^[a-z]{1,6}_[A-Za-z0-9_-]{4,}$/;
/** md5 / sha digests used as cache keys. */
const HEX_DIGEST_PATTERN = /^[0-9a-f]{24,}$/i;

export function isIdLike(segment: string): boolean {
  return (
    /^\d+$/.test(segment) ||
    UUID_PATTERN.test(segment) ||
    SEQUENCE_ID_PATTERN.test(segment) ||
    PREFIXED_ID_PATTERN.test(segment) ||
    HEX_DIGEST_PATTERN.test(segment)
  );
}

/** Good enough for the resource names that precede an id in our URLs. */
export function singularize(word: string): string {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/** Single words that end in `s` but are not plural collections. */
const NOT_PLURAL: ReadonlySet<string> = new Set(['status', 'canvas', 'analysis', 'focus']);

/**
 * A plural single word (`requests`, `notes`, `folders`) names a collection, so a
 * POST to it creates something. A hyphenated phrase (`download-assets`) or a
 * word ending in `ss` (`compress`) is an action verb.
 */
function looksPlural(word: string): boolean {
  if (word.includes('-') || word.endsWith('ss') || NOT_PLURAL.has(word)) return false;
  return word.endsWith('s');
}

export interface DerivedAction {
  action: string;
  entityType: string | null;
  entityId: string | null;
}

function pathSegments(pathname: string): string[] {
  const clean = pathname.split(/[?#]/)[0];
  return clean.split('/').filter(Boolean);
}

/**
 * `POST /api/concepts/export` -> `concepts.export`, `PUT /api/concepts/5` ->
 * `concepts.update` with entity concept 5, and so on. See the design doc for
 * the full rule table; the tests are the executable version of it.
 */
export function deriveApiAction(method: string, pathname: string): DerivedAction {
  const verb = method.toUpperCase();
  // Ids keep their case (`PR-7` is an id, `pr-7` would not be); words are lowercased.
  const segments = pathSegments(pathname);
  if (segments[0]?.toLowerCase() === 'api') segments.shift();

  const words: string[] = [];
  let entityType: string | null = null;
  let entityId: string | null = null;

  segments.forEach((segment, index) => {
    if (isIdLike(segment)) {
      if (entityId === null) {
        entityId = segment;
        const previous = index > 0 ? segments[index - 1] : null;
        entityType = previous && !isIdLike(previous) ? singularize(previous.toLowerCase()) : null;
      }
      return;
    }
    words.push(segment.toLowerCase());
  });

  const base = words.length > 0 ? words.join('.') : 'api.unknown';
  if (words.length === 0 && segments.length === 0) {
    return { action: base, entityType, entityId };
  }

  const last = words[words.length - 1] ?? '';
  const endsWithId = segments.length > 0 && isIdLike(segments[segments.length - 1]);
  const isCollection = words.length === 1 || looksPlural(last);

  let action = base;
  if (verb === 'POST') {
    if (endsWithId || isCollection) action = `${base}.create`;
  } else if (verb === 'PUT' || verb === 'PATCH') {
    action = `${base}.update`;
  } else if (verb === 'DELETE') {
    if (!/delete|remove/.test(last)) action = `${base}.delete`;
  }

  return { action, entityType, entityId };
}

/** `/playable/2f1c…` -> `/playable/:id`; the query (already reduced to section/tab) is kept. */
export function normalizePagePath(path: string): string {
  const [pathname, query] = path.split('?');
  const normalized = pathname
    .split('/')
    .map(segment => (segment && isIdLike(segment) ? ':id' : segment))
    .join('/');
  return query ? `${normalized}?${query}` : normalized;
}

/**
 * The page identity to log: pathname plus the `section` / `tab` keys, which are
 * how the dashboard and the workflow workspaces switch views without changing
 * route. Every other query key (return URLs, tokens, filters) is dropped.
 */
export function pagePathWithContext(pathname: string, search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const kept = new URLSearchParams();
  for (const key of PAGE_CONTEXT_KEYS) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const query = kept.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** Should the browser tracker record this fetch? */
export function shouldTrackApiCall(method: string, url: URL, origin: string): boolean {
  if (!MUTATING_METHODS.has(method.toUpperCase())) return false;
  if (url.origin !== origin) return false;
  if (!url.pathname.startsWith('/api/')) return false;
  return !IGNORED_API_PREFIXES.some(prefix => url.pathname.startsWith(prefix));
}
